import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { toast } from "sonner";
import {
  Search, Sparkles, TrendingUp, ShieldCheck, Zap, ArrowRight, X, Loader2, Info,
  CheckCircle2, XCircle, HelpCircle, Share2, Printer, History, Trash2, ListChecks,
  Download, Cloud,
} from "lucide-react";
import { PageShell } from "@/components/site/page-shell";
import { estimateDomainValue, estimateDomainValueBulk, type DomainValuation } from "@/lib/domain-value.functions";
import { getDomainValuePreferences, setDomainValuePreferences } from "@/lib/domain-value-preferences.functions";
import { normalizeDomain, validateDomain } from "@/lib/domain-value.pure";
import { CATEGORY_META, type FactorCategory, type ValuationFactor } from "@/lib/domain-value.pure";
import { useAuth } from "@/hooks/use-auth";

const TITLE = "Domain Value Checker — Instant Estimated Price & Availability";
const DESC = "Free AI-powered domain appraisal. Get an instant estimated market value, quality score, brandability analysis and live availability for any domain.";

export const Route = createFileRoute("/domain-value")({
  component: DomainValuePage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/domain-value" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/domain-value" }],
  }),
});

const DEFAULT_POPULAR = ["coderlity.com", "digitalseo.net", "codexox.com", "google.com", "facebook.com"];
const RECENT_KEY = "domain-value:recent";
const CHIPS_KEY = "domain-value:chips";

function loadChips(): string[] {
  if (typeof window === "undefined") return DEFAULT_POPULAR;
  try {
    const raw = localStorage.getItem(CHIPS_KEY);
    if (!raw) return DEFAULT_POPULAR;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed.slice(0, 10) : DEFAULT_POPULAR;
  } catch { return DEFAULT_POPULAR; }
}

function saveChipsLocal(list: string[]) {
  try { localStorage.setItem(CHIPS_KEY, JSON.stringify(list.slice(0, 10))); } catch { /* ignore */ }
}


const FEATURES = [
  { icon: Zap, title: "Instant estimate", desc: "Real-time algorithmic pricing — no signup, no waiting." },
  { icon: Sparkles, title: "AI brandability", desc: "Gemini evaluates memorability and best-fit industry." },
  { icon: TrendingUp, title: "Score & factors", desc: "See exactly why a domain is priced high or low." },
  { icon: ShieldCheck, title: "Live availability", desc: "RDAP check tells you if it can be registered today." },
];

const FAQ = [
  { q: "Is the estimate accurate?", a: "It's a data-driven starting point based on TLD, length, keyword strength and brandability heuristics — not a formal appraisal." },
  { q: "Where does availability come from?", a: "We query the public RDAP registry in real time. Some ccTLDs may return unknown." },
  { q: "Can I check many domains at once?", a: "Yes — switch to Bulk mode and paste up to 25 domains." },
  { q: "How do I share or save a report?", a: "Use Share to copy a link, or Print/Save PDF to download a clean report." },
];

function formatUsd(n: number) {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

type Recent = { domain: string; value_usd: number; score: number; at: number };

function loadRecents(): Recent[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, 8) : [];
  } catch { return []; }
}

function pushRecent(next: Recent) {
  const list = loadRecents().filter((r) => r.domain !== next.domain);
  list.unshift(next);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 8)));
}

function DomainValuePage() {
  const { user } = useAuth();
  const [mode, setMode] = useState<"single" | "bulk">("single");
  const [query, setQuery] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [recents, setRecents] = useState<Recent[]>([]);
  const [chips, setChips] = useState<string[]>(DEFAULT_POPULAR);
  const [editChips, setEditChips] = useState(false);
  const [newChip, setNewChip] = useState("");
  const [chipsSynced, setChipsSynced] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  const runEstimate = useServerFn(estimateDomainValue);
  const runBulk = useServerFn(estimateDomainValueBulk);
  const runGetPrefs = useServerFn(getDomainValuePreferences);
  const runSetPrefs = useServerFn(setDomainValuePreferences);

  const mutation = useMutation({
    mutationFn: (domain: string) => runEstimate({ data: { domain } }),
    onSuccess: (data) => {
      if (data.ok) {
        pushRecent({ domain: data.domain, value_usd: data.value_usd, score: data.score, at: Date.now() });
        setRecents(loadRecents());
      }
    },
  });
  const bulk = useMutation({
    mutationFn: (domains: string[]) => runBulk({ data: { domains } }),
  });

  useEffect(() => {
    setRecents(loadRecents());
    setChips(loadChips());
    // Deep-link support: ?domain=example.com
    if (typeof window !== "undefined") {
      const p = new URLSearchParams(window.location.search).get("domain");
      if (p) {
        const n = normalizeDomain(p);
        setQuery(n);
        if (!validateDomain(n)) mutation.mutate(n);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hydrate chips from the user's profile once signed in.
  useEffect(() => {
    if (!user) { setChipsSynced(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await runGetPrefs();
        if (cancelled) return;
        if (res.chips && res.chips.length) {
          setChips(res.chips);
          saveChipsLocal(res.chips);
        }
        setChipsSynced(true);
      } catch {
        setChipsSynced(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, runGetPrefs]);

  const persistChips = (next: string[]) => {
    saveChipsLocal(next);
    if (user) {
      runSetPrefs({ data: { chips: next } }).catch(() => {
        toast.error("Couldn't sync examples to your profile");
      });
    }
  };

  const result = mutation.data && mutation.data.ok ? (mutation.data as DomainValuation) : null;
  const normalizedPreview = useMemo(() => normalizeDomain(query), [query]);
  const validationError = useMemo(() => (query.trim() ? validateDomain(query) : null), [query]);
  const bulkList = useMemo(() => (
    bulkText.split(/[\s,;\n]+/).map((s) => normalizeDomain(s)).filter(Boolean).slice(0, 25)
  ), [bulkText]);
  const bulkInvalid = useMemo(() => bulkList.filter((d) => validateDomain(d)), [bulkList]);

  const onQueryChange = (v: string) => {
    // Normalize on paste of a full URL, keep raw text otherwise so user can type freely
    if (/\s|https?:\/\//i.test(v)) setQuery(normalizeDomain(v));
    else setQuery(v.toLowerCase());
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const n = normalizeDomain(query);
    const err = validateDomain(n);
    if (err) { toast.error(err); return; }
    setQuery(n);
    mutation.mutate(n);
  };
  const onBulkSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!bulkList.length) return;
    bulk.mutate(bulkList);
  };
  const onPick = (d: string) => {
    const n = normalizeDomain(d);
    setQuery(n);
    if (!validateDomain(n)) mutation.mutate(n);
  };

  const addChip = () => {
    const n = normalizeDomain(newChip);
    const err = validateDomain(n);
    if (err) { toast.error(err); return; }
    if (chips.includes(n)) { toast.error("Already in the list"); return; }
    const next = [...chips, n].slice(0, 10);
    setChips(next); persistChips(next); setNewChip("");
  };
  const removeChip = (d: string) => {
    const next = chips.filter((x) => x !== d);
    setChips(next); persistChips(next);
  };
  const resetChips = () => {
    setChips(DEFAULT_POPULAR);
    persistChips(DEFAULT_POPULAR);
    toast.success("Examples reset");
  };



  const shareUrl = result ? `${typeof window !== "undefined" ? window.location.origin : ""}/domain-value?domain=${encodeURIComponent(result.domain)}` : "";
  const onShare = async () => {
    if (!result) return;
    const shareData = {
      title: `Domain value: ${result.domain}`,
      text: `${result.domain} — est. ${formatUsd(result.value_usd)} (score ${result.score}/100)`,
      url: shareUrl,
    };
    try {
      if (typeof navigator !== "undefined" && (navigator as any).share) {
        await (navigator as any).share(shareData);
        return;
      }
    } catch { /* fall through */ }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Report link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const onPrint = () => {
    if (!result) return;
    window.print();
  };

  const clearRecents = () => {
    localStorage.removeItem(RECENT_KEY);
    setRecents([]);
  };

  return (
    <PageShell>
      <style>{PRINT_CSS}</style>

      {/* Hero + search */}
      <section className="dv-no-print relative overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 -z-10 bg-hero-glow opacity-70" />
        <div className="absolute inset-0 -z-10 [mask-image:radial-gradient(circle_at_center,black,transparent_70%)] bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:44px_44px] opacity-30" />
        <div className="mx-auto max-w-4xl px-4 lg:px-8 pt-8 pb-6 sm:pt-12 sm:pb-8 text-center">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card/70 px-2.5 py-0.5 text-[11px] font-semibold text-primary backdrop-blur">
            <Sparkles className="h-3 w-3" /> AI appraisal · free
          </div>
          <h1 className="mt-2 text-3xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
            What's your <span className="bg-gradient-brand bg-clip-text text-transparent">domain</span> worth?
          </h1>
          <p className="mt-2 max-w-xl mx-auto text-sm sm:text-base text-muted-foreground">
            Instant price, quality tier &amp; live availability — single or bulk.
          </p>

          {/* Mode toggle */}
          <div className="mt-4 inline-flex rounded-full border border-border bg-card p-0.5 text-xs sm:text-sm font-semibold">
            <button
              type="button" onClick={() => setMode("single")}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 transition ${mode === "single" ? "bg-gradient-brand text-white shadow-glow" : "text-muted-foreground hover:text-foreground"}`}
            >
              <Search className="h-3.5 w-3.5" /> Single
            </button>
            <button
              type="button" onClick={() => setMode("bulk")}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 transition ${mode === "bulk" ? "bg-gradient-brand text-white shadow-glow" : "text-muted-foreground hover:text-foreground"}`}
            >
              <ListChecks className="h-3.5 w-3.5" /> Bulk
            </button>
          </div>

          {mode === "single" ? (
            <form onSubmit={onSubmit} className="mt-4">
              <div className="mx-auto flex max-w-2xl items-stretch gap-1.5 rounded-2xl border border-border bg-card/80 p-1.5 shadow-lg backdrop-blur">
                <div className="flex flex-1 items-center gap-2 rounded-xl px-3 min-w-0">
                  <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <input
                    autoFocus value={query}
                    onChange={(e) => onQueryChange(e.target.value)}
                    onPaste={(e) => {
                      const text = e.clipboardData.getData("text");
                      if (/\s|https?:\/\/|www\./i.test(text) || text !== text.toLowerCase()) {
                        e.preventDefault();
                        setQuery(normalizeDomain(text));
                      }
                    }}
                    placeholder="yourbrand.com"
                    aria-invalid={!!validationError}
                    className={`min-w-0 flex-1 bg-transparent py-2.5 text-sm sm:text-base outline-none placeholder:text-muted-foreground/70 ${validationError ? "text-destructive" : ""}`}
                    autoCapitalize="off" autoCorrect="off" spellCheck={false}
                  />
                  {query && (
                    <button type="button" onClick={() => setQuery("")} className="rounded-full p-1 text-muted-foreground hover:text-foreground">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <button
                  type="submit" disabled={mutation.isPending || !query.trim() || !!validationError}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-gradient-brand px-4 py-2.5 text-sm font-semibold text-white shadow-glow disabled:opacity-60"
                >
                  {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  <span className="hidden sm:inline">{mutation.isPending ? "Analyzing…" : "Get value"}</span>
                </button>
              </div>

              {query.trim() && !validationError && normalizedPreview !== query && (
                <div className="mx-auto mt-2 flex max-w-2xl items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs text-foreground/80">
                  <Info className="h-3.5 w-3.5 shrink-0 text-primary" />
                  <span>Will check:&nbsp;</span>
                  <code className="font-mono font-semibold text-primary">{normalizedPreview}</code>
                </div>
              )}

              {validationError && (
                <div className="mx-auto mt-2 flex max-w-2xl items-center gap-1.5 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-1.5 text-xs text-destructive">
                  <XCircle className="h-3.5 w-3.5 shrink-0" /> {validationError}
                </div>
              )}

              <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5 text-[11px]">
                <span className="text-muted-foreground">Try:</span>
                {chips.map((d) => (
                  <span key={d} className="inline-flex items-center overflow-hidden rounded-full border border-border bg-card">
                    <button type="button" onClick={() => onPick(d)}
                      className="px-2.5 py-0.5 font-medium hover:text-primary">
                      {d}
                    </button>
                    {editChips && (
                      <button type="button" onClick={() => removeChip(d)} aria-label={`Remove ${d}`}
                        className="border-l border-border px-1.5 py-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                ))}
                <button type="button" onClick={() => setEditChips((v) => !v)}
                  className="ml-1 rounded-full border border-dashed border-border px-2 py-0.5 text-muted-foreground hover:border-primary/40 hover:text-primary">
                  {editChips ? "Done" : "Customize"}
                </button>
                {editChips && (
                  <button type="button" onClick={resetChips}
                    className="rounded-full border border-dashed border-border px-2 py-0.5 text-muted-foreground hover:text-foreground">
                    Reset
                  </button>
                )}
                {user && chipsSynced && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary" title="Saved to your profile — synced across devices">
                    <Cloud className="h-3 w-3" /> Synced
                  </span>
                )}
              </div>

              {editChips && (
                <div className="mx-auto mt-2 flex max-w-md items-center gap-1.5">
                  <input
                    value={newChip}
                    onChange={(e) => setNewChip(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addChip(); } }}
                    placeholder="Add example (e.g. mybrand.com)"
                    className="flex-1 rounded-full border border-border bg-card px-3 py-1 text-xs outline-none focus:border-primary/40"
                    autoCapitalize="off" autoCorrect="off" spellCheck={false}
                  />
                  <button type="button" onClick={addChip}
                    className="rounded-full bg-gradient-brand px-3 py-1 text-xs font-semibold text-white shadow-glow">
                    Add
                  </button>
                </div>
              )}
            </form>

          ) : (
            <form onSubmit={onBulkSubmit} className="mt-4 text-left">
              <div className="mx-auto max-w-2xl rounded-2xl border border-border bg-card/80 p-2.5 shadow-lg backdrop-blur">
                <textarea
                  value={bulkText} onChange={(e) => setBulkText(e.target.value)}
                  rows={4}
                  placeholder={"Paste one domain per line (up to 25)\nexample.com\nnova.ai\ngetpay.co"}
                  className="w-full resize-y bg-transparent p-2 text-sm outline-none placeholder:text-muted-foreground/70"
                  spellCheck={false} autoCapitalize="off" autoCorrect="off"
                />
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-2">
                  <span className="text-xs text-muted-foreground">
                    {bulkList.length} / 25 domains
                    {bulkInvalid.length > 0 && (
                      <span className="ml-2 text-destructive">· {bulkInvalid.length} invalid</span>
                    )}
                  </span>
                  <button type="submit" disabled={bulk.isPending || !bulkList.length}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-brand px-4 py-2 text-sm font-semibold text-white shadow-glow disabled:opacity-60">
                    {bulk.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListChecks className="h-4 w-4" />}
                    {bulk.isPending ? "Analyzing…" : "Check all"}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Recent + Result */}
      <section className="border-b border-border/60">
        <div className="mx-auto max-w-5xl px-4 lg:px-8 py-6 sm:py-8">
          {/* Recents */}
          {mode === "single" && recents.length > 0 && (
            <div className="dv-no-print mb-8 rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <History className="h-4 w-4 text-primary" /> Recent searches
                </div>
                <button onClick={clearRecents} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-3.5 w-3.5" /> Clear
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {recents.map((r) => (
                  <button
                    key={r.domain} onClick={() => onPick(r.domain)}
                    className="group flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-xs hover:border-primary/40"
                  >
                    <span className="font-semibold">{r.domain}</span>
                    <span className="text-muted-foreground">{formatUsd(r.value_usd)}</span>
                    <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary">{r.score}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {mutation.isError && (
            <div className="dv-no-print rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
              {(mutation.error as Error)?.message || "Could not estimate. Please try again."}
            </div>
          )}

          {mode === "single" && result && (
            <div ref={reportRef} className="dv-report rounded-3xl border border-border bg-card p-6 sm:p-8 shadow-lg">
              {/* Header row */}
              <div className="grid grid-cols-[minmax(0,1fr)] gap-6 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                <div className="min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Estimated value</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <div className="truncate text-lg font-semibold">{result.domain}</div>
                    <AvailabilityBadge available={result.available} />
                    <QualityBadge tier={result.quality.tier} />
                  </div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-4xl sm:text-5xl font-bold text-primary">{formatUsd(result.value_usd)}</span>
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    Range: {formatUsd(result.range_low_usd)} – {formatUsd(result.range_high_usd)}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{result.quality.description}</div>
                </div>
                <div className="flex shrink-0 flex-col items-center">
                  <ScoreDial score={result.score} />
                  <span className="mt-1 text-xs text-muted-foreground">Quality score</span>
                </div>
              </div>

              {/* Actions */}
              <div className="dv-no-print mt-5 flex flex-wrap gap-2">
                <button onClick={onShare} className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold hover:border-primary/40">
                  <Share2 className="h-4 w-4" /> Share
                </button>
                <button onClick={onPrint} className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold hover:border-primary/40">
                  <Printer className="h-4 w-4" /> Print
                </button>
                <button onClick={onPrint} className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold hover:border-primary/40">
                  <Download className="h-4 w-4" /> Save PDF
                </button>
                {result.available === true ? (
                  <Link to="/hosting/domains" className="inline-flex items-center gap-2 rounded-full bg-gradient-brand px-4 py-2 text-sm font-semibold text-white shadow-glow">
                    Register now <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : result.available === false ? (
                  <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-gradient-brand px-4 py-2 text-sm font-semibold text-white shadow-glow">
                    Try brokerage <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : null}
              </div>

              {result.narrative && (
                <div className="mt-6 flex gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <p className="min-w-0 text-foreground/90">{result.narrative}</p>
                </div>
              )}

              {/* Breakdown — grouped by category */}
              <div className="mt-6">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Why this price</div>
                  <div className="text-xs text-muted-foreground">{result.factors.length} factors</div>
                </div>
                <GroupedBreakdown factors={result.factors} />
              </div>

              {/* Summary rail — nice on print */}
              <div className="mt-6 grid gap-3 rounded-2xl border border-border bg-background/50 p-4 sm:grid-cols-5">
                <SummaryStat label="TLD" value={`.${result.tld}`} />
                <SummaryStat label="Length" value={`${result.sld.length} chars`} />
                <SummaryStat label="Quality" value={result.quality.tier} />
                <SummaryStat label="Availability" value={result.available === true ? "Available" : result.available === false ? "Taken" : "Unknown"} />
                <SummaryStat label="Final score" value={`${result.score} / 100`} />
              </div>

              <p className="dv-no-print mt-4 flex items-start gap-2 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Estimates are algorithmic — a starting point, not a formal appraisal.
              </p>
              <p className="dv-print-only mt-6 text-[11px] text-muted-foreground">
                Report generated at {new Date().toLocaleString()} · {shareUrl || "/domain-value"}
              </p>
            </div>
          )}

          {mode === "bulk" && bulk.data && (
            <div className="dv-report overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
                <div className="font-semibold">Bulk results ({bulk.data.results.length})</div>
                <div className="dv-no-print flex gap-2">
                  <button onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:border-primary/40">
                    <Printer className="h-3.5 w-3.5" /> Print / PDF
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3">Domain</th>
                      <th className="px-4 py-3">Estimate</th>
                      <th className="px-4 py-3">Range</th>
                      <th className="px-4 py-3">Quality</th>
                      <th className="px-4 py-3">Score</th>
                      <th className="px-4 py-3">Availability</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulk.data.results.map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-4 py-3 font-semibold">{r.ok ? r.domain : r.input}</td>
                        <td className="px-4 py-3">{r.ok ? formatUsd(r.value_usd) : "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground">{r.ok ? `${formatUsd(r.range_low_usd)}–${formatUsd(r.range_high_usd)}` : "—"}</td>
                        <td className="px-4 py-3">{r.ok ? <QualityBadge tier={r.quality.tier} /> : "—"}</td>
                        <td className="px-4 py-3">{r.ok ? <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">{r.score}</span> : <span className="text-destructive text-xs">invalid</span>}</td>
                        <td className="px-4 py-3">{r.ok ? <AvailabilityBadge available={r.available} /> : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Features */}
      <section className="dv-no-print border-b border-border/60">
        <div className="mx-auto max-w-6xl px-4 lg:px-8 py-14 sm:py-20">
          <div className="text-center"><h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Why use our checker</h2></div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-2xl border border-border bg-card p-5 transition hover:border-primary/40">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><f.icon className="h-5 w-5" /></div>
                <h3 className="mt-4 text-base font-semibold">{f.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="dv-no-print border-b border-border/60">
        <div className="mx-auto max-w-4xl px-4 lg:px-8 py-14 sm:py-20">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">FAQ</h2>
          <div className="mt-6 divide-y divide-border rounded-2xl border border-border bg-card">
            {FAQ.map((f) => (
              <details key={f.q} className="group p-5 open:bg-muted/20">
                <summary className="flex cursor-pointer items-center justify-between gap-4 text-base font-semibold">
                  <span className="min-w-0">{f.q}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 transition group-open:rotate-90" />
                </summary>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function AvailabilityBadge({ available }: { available: boolean | null }) {
  if (available === true)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-500">
        <CheckCircle2 className="h-3.5 w-3.5" /> Available
      </span>
    );
  if (available === false)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-xs font-semibold text-destructive">
        <XCircle className="h-3.5 w-3.5" /> Taken
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">
      <HelpCircle className="h-3.5 w-3.5" /> Unknown
    </span>
  );
}

function GroupedBreakdown({ factors }: { factors: ValuationFactor[] }) {
  const order: FactorCategory[] = ["brand", "extension", "length", "dictionary", "keyword", "phonetics", "structure"];
  const grouped = order
    .map((cat) => ({ cat, items: factors.filter((f) => f.category === cat) }))
    .filter((g) => g.items.length > 0);
  const totalImpact = (items: ValuationFactor[]) => items.reduce((sum, f) => sum + f.impact, 0);
  return (
    <div className="mt-3 grid gap-3 sm:grid-cols-2">
      {grouped.map(({ cat, items }) => {
        const t = totalImpact(items);
        const meta = CATEGORY_META[cat];
        const positive = t >= 0;
        return (
          <div key={cat} className="rounded-xl border border-border bg-background/50 p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold">{meta.title}</div>
                <div className="text-[11px] text-muted-foreground">{meta.description}</div>
              </div>
              <span className={`shrink-0 rounded-md px-2 py-0.5 text-xs font-bold ${positive ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive"}`}>
                {positive ? `+${t}` : t}
              </span>
            </div>
            <ul className="mt-2 space-y-1.5">
              {items.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-xs">
                  <span className={`mt-0.5 grid h-5 w-8 shrink-0 place-items-center rounded text-[10px] font-bold ${f.impact >= 0 ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive"}`}>
                    {f.impact >= 0 ? `+${f.impact}` : f.impact}
                  </span>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{f.label}</div>
                    <div className="text-muted-foreground">{f.note}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function QualityBadge({ tier }: { tier: string }) {
  const styles: Record<string, string> = {
    "Ultra Premium": "bg-gradient-to-r from-amber-500/20 to-fuchsia-500/20 text-amber-500 border-amber-500/30",
    "Premium": "bg-primary/15 text-primary border-primary/30",
    "Standard": "bg-sky-500/15 text-sky-500 border-sky-500/30",
    "Developer": "bg-muted text-foreground/80 border-border",
    "Basic": "bg-muted text-muted-foreground border-border",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${styles[tier] ?? "bg-muted text-muted-foreground border-border"}`}>
      <Sparkles className="h-3 w-3" /> {tier}
    </span>
  );
}


function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-1 truncate text-sm font-semibold">{value}</div>
    </div>
  );
}

function ScoreDial({ score }: { score: number }) {
  const clamped = Math.max(0, Math.min(100, score));
  const r = 28;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - clamped / 100);
  const color = clamped >= 75 ? "hsl(var(--primary))" : clamped >= 45 ? "#f59e0b" : "#ef4444";
  return (
    <div className="relative h-20 w-20">
      <svg viewBox="0 0 72 72" className="h-20 w-20 -rotate-90" aria-hidden="true">
        <circle cx="36" cy="36" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="6" />
        <circle cx="36" cy="36" r={r} fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 600ms ease" }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-lg font-bold">{clamped}</div>
    </div>
  );
}

const PRINT_CSS = `
.dv-print-only { display: none; }
@media print {
  @page { margin: 16mm; }
  html, body { background: white !important; }
  header, footer, nav, .dv-no-print { display: none !important; }
  .dv-report { box-shadow: none !important; border: none !important; padding: 0 !important; }
  .dv-print-only { display: block; }
  details > summary { list-style: none; }
  a { color: inherit; text-decoration: none; }
}
`;
